import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  apiClient,
  ApiError,
  type OrgSkillDetail,
  type OrgSkillSummary,
  type TeamDraft,
} from '../lib/api';
import { canApprove, getSession } from '../lib/auth';
import { useOrgSse } from '../lib/sse';

export function TeamSkill() {
  const params = useParams();
  const navigate = useNavigate();
  const session = getSession();
  const orgId = decodeURIComponent(params.orgId ?? '');
  const skillId = decodeURIComponent(params.skillId ?? '');

  const [summary, setSummary] = useState<OrgSkillSummary | null>(null);
  const [skill, setSkill] = useState<OrgSkillDetail | null>(null);
  const [openDraft, setOpenDraft] = useState<TeamDraft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await apiClient.orgs.listSkills(orgId);
        const match = list.skills.find((s) => s.id === skillId);
        if (!match) {
          if (!cancelled) setError('Skill not found in this org.');
          return;
        }
        if (cancelled) return;
        setSummary(match);
        const detail = await apiClient.orgs.getSkill(orgId, match.skillPath);
        if (cancelled) return;
        setSkill(detail);
        const draft = await apiClient.orgs.drafts.myOpenForSkill(orgId, match.id);
        if (cancelled) return;
        setOpenDraft(draft);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof ApiError ? err.message : 'Failed to load skill');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, skillId]);

  useEffect(() => load(), [load]);
  useOrgSse(orgId, { onHint: load });

  const handleSuggest = async () => {
    if (!summary || !skill) return;
    setBusy(true);
    setError(null);
    try {
      const draft = await apiClient.orgs.drafts.start(orgId, {
        skill_id: summary.id,
        body: skill.content,
        base_version_hash: summary.latestCommitSha,
      });
      navigate(`/orgs/${encodeURIComponent(orgId)}/drafts/${encodeURIComponent(draft.id)}/edit`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to start draft');
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return <ErrorView orgId={orgId} message={error} />;
  }
  if (!summary || !skill) {
    return <LoadingView orgId={orgId} />;
  }

  return (
    <div className="grid h-full grid-cols-1 md:grid-cols-[280px_1fr]">
      <aside className="hairline-b md:hairline-b-0 md:hairline-r flex flex-col bg-surface p-3 text-[12px]">
        <Link to="/library" className="mb-2 text-text-muted hover:text-[var(--color-accent)]">
          ← Library
        </Link>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-text-subtle">
          Team skill
        </div>
        <div className="mt-1 font-mono text-[13px]">{summary.skillPath}</div>
        <div className="mt-2 break-all font-mono text-[10px] text-text-subtle">
          sha {summary.latestCommitSha.slice(0, 12)}
        </div>
        {canApprove(session) && (
          <Link
            to={`/orgs/${encodeURIComponent(orgId)}/inbox`}
            className="btn mt-4 justify-center"
          >
            Review inbox
          </Link>
        )}
        {openDraft && openDraft.state !== 'approved' && (
          <Link
            to={`/orgs/${encodeURIComponent(orgId)}/drafts/${encodeURIComponent(openDraft.id)}/edit`}
            className="btn btn-primary mt-3 justify-center"
          >
            Continue your draft
          </Link>
        )}
        {(!openDraft || openDraft.state === 'approved') && (
          <button
            type="button"
            onClick={handleSuggest}
            disabled={busy}
            className="btn btn-primary mt-3 justify-center"
          >
            {busy ? 'Starting…' : 'Suggest a change'}
          </button>
        )}
      </aside>

      <section className="flex flex-col overflow-hidden bg-canvas">
        <header className="hairline-b flex items-center justify-between px-4 py-2">
          <h1 className="font-mono text-[13px]">{summary.skillPath}</h1>
          <span className="text-[11px] text-text-subtle">Read-only — team source of truth</span>
        </header>
        <pre className="m-0 flex-1 overflow-y-auto whitespace-pre-wrap px-6 py-4 font-mono text-[13px] leading-relaxed">
          {skill.content}
        </pre>
      </section>
    </div>
  );
}

function LoadingView({ orgId: _orgId }: { orgId: string }) {
  return (
    <main className="flex h-full items-center justify-center text-[12px] text-text-subtle">
      Loading team skill…
    </main>
  );
}

function ErrorView({ orgId, message }: { orgId: string; message: string }) {
  return (
    <main className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
      <p className="text-sm text-[var(--color-danger)]">{message}</p>
      <Link
        to="/library"
        className="text-[12px] underline hover:text-[var(--color-accent)]"
      >
        Back to library
      </Link>
      <span className="font-mono text-[10px] text-text-subtle">org {orgId}</span>
    </main>
  );
}
